import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Newspaper, Calendar, Layers, Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const CATEGORY_STYLES = {
  Noticias: "bg-cyan-500/20 text-cyan-300 border-cyan-500/40",
  Banlist: "bg-red-500/20 text-red-300 border-red-500/40",
  "Deck Profile": "bg-purple-500/20 text-purple-300 border-purple-500/40",
  "Reporte de Torneo": "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
};

export const formatDate = (d) => {
  if (!d) return "";
  const date = new Date(d + (d.length === 10 ? "T12:00:00" : ""));
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
};

const PostCard = ({ post, featured }) => (
  <Link to={`/noticias/${post.slug}`} className={`group relative rounded-2xl overflow-hidden glass-card border border-purple-500/20 hover:border-pink-500/50 transition-all duration-300 hover:-translate-y-1 flex flex-col ${featured ? "md:col-span-2 md:flex-row" : ""}`} data-testid={`news-card-${post.slug}`}>
    <div className={`relative overflow-hidden ${featured ? "md:w-1/2 h-56 md:h-auto" : "h-44"}`}>
      {post.imagen_portada ? <img src={post.imagen_portada} alt={post.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full bg-gradient-to-br from-purple-900 to-black" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0512] via-transparent to-transparent" />
      <div className="absolute top-3 left-3 flex gap-2">
        <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${CATEGORY_STYLES[post.categoria] || CATEGORY_STYLES.Noticias}`}>{post.categoria}</span>
        {post.destacado && <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-yellow-400 text-black flex items-center gap-1"><Star size={10} /> Destacado</span>}
      </div>
    </div>
    <div className="p-4 flex flex-col flex-1">
      <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-2">
        <span className="flex items-center gap-1"><Calendar size={11} /> {formatDate(post.fecha)}</span>
        <span className="px-1.5 py-0.5 rounded bg-purple-800/50 text-purple-200">{post.juego}</span>
        {post.decklist?.main?.length > 0 && <span className="flex items-center gap-1 text-pink-300"><Layers size={11} /> Decklist</span>}
      </div>
      <h3 className={`font-black text-white leading-snug group-hover:text-yellow-300 transition-colors ${featured ? "text-xl md:text-2xl" : "text-base"}`}>{post.titulo}</h3>
      {post.resumen && <p className="text-sm text-gray-400 mt-2 line-clamp-3">{post.resumen}</p>}
      <span className="mt-auto pt-3 text-xs font-bold text-pink-400">Leer más →</span>
    </div>
  </Link>
);

export default function NewsPage() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState({ categorias: [], juegos: [] });
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [categoria, setCategoria] = useState("");
  const [juego, setJuego] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const LIMIT = 9;

  useEffect(() => { axios.get(`${API}/news/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => setDebouncedQ(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { setPage(0); }, [debouncedQ, categoria, juego]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ skip: page * LIMIT, limit: LIMIT });
    if (debouncedQ) params.set("q", debouncedQ);
    if (categoria) params.set("categoria", categoria);
    if (juego) params.set("juego", juego);
    axios.get(`${API}/news?${params}`).then((r) => { setPosts(r.data.posts || []); setTotal(r.data.total || 0); }).catch(() => setPosts([])).finally(() => setLoading(false));
  }, [debouncedQ, categoria, juego, page]);

  const pages = Math.ceil(total / LIMIT);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16" data-testid="news-page">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div className="max-w-2xl">
            <p className="text-xs tracking-[0.3em] text-cyan-400 font-bold mb-3">FEED NIRVANA</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-none mb-3">
              <span className="bg-gradient-to-r from-cyan-400 via-pink-500 to-yellow-400 bg-clip-text text-transparent">Noticias</span>
            </h1>
            <p className="text-gray-400 text-base md:text-lg">Eventos, banlists, deck profiles y reportes de torneo de todos los juegos del circuito.</p>
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar noticias, decks, torneos..." className="w-full pl-10 pr-4 py-2.5 bg-purple-900/40 border border-purple-700/50 rounded-xl text-white text-sm placeholder-gray-500 focus:outline-none focus:border-pink-500" data-testid="news-search-input" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="news-filters">
          <button onClick={() => setCategoria("")} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${!categoria ? "bg-pink-500 text-white border-pink-500" : "border-purple-700/60 text-gray-300 hover:border-pink-500/60"}`} data-testid="news-cat-all">Todas</button>
          {meta.categorias.map((c) => (
            <button key={c} onClick={() => setCategoria(c)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${categoria === c ? "bg-pink-500 text-white border-pink-500" : "border-purple-700/60 text-gray-300 hover:border-pink-500/60"}`} data-testid={`news-cat-${c.toLowerCase().replace(/ /g, "-")}`}>{c}</button>
          ))}
          <select value={juego} onChange={(e) => setJuego(e.target.value)} className="ml-auto px-3 py-1.5 bg-purple-900/50 border border-purple-700/50 rounded-lg text-white text-xs focus:outline-none focus:border-pink-500" data-testid="news-game-filter">
            <option value="">Todos los juegos</option>
            {meta.juegos.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{[1, 2, 3].map((i) => <div key={i} className="h-72 rounded-2xl bg-purple-900/20 animate-pulse" />)}</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 glass-card rounded-2xl" data-testid="news-empty"><Newspaper size={40} className="mx-auto text-gray-600 mb-3" /><p className="text-gray-400">No se encontraron publicaciones</p></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="news-grid">
            {posts.map((p, i) => <PostCard key={p.id} post={p} featured={page === 0 && i === 0 && !debouncedQ} />)}
          </div>
        )}

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-10" data-testid="news-pagination">
            {Array.from({ length: pages }).map((_, i) => (
              <button key={i} onClick={() => setPage(i)} className={`w-9 h-9 rounded-lg text-sm font-bold ${page === i ? "bg-pink-500 text-white" : "bg-purple-900/40 text-gray-300 hover:bg-purple-800/60"}`}>{i + 1}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
